import { push } from 'redux-first-history';
import { all, takeEvery, put, call } from 'redux-saga/effects';
import actions from './actions';
import Api from '../../../../helpers/api';
import { sendRequest } from '../../../../helpers/saga';
import { logger } from '../../../../helpers/logger';
import notifications from '../../../../redux/notifications/actions';
import { keyByProperty } from '../../../../helpers/core';
import { t } from '../../../../i18n';

const api = new Api('workers/:id/superannuation_information');
const apiSuperfundList = new Api('workers/:id/superannuation_information/superfund_list');
const formInfoApi = new Api('workers/:id/superannuation_information/form_data');

function* loadDataRequest({ payload }) {
  try {
    const { id } = payload;
    const result = yield call(sendRequest, api.load, { id }, notifications.REQUEST_ERROR);

    if (result && result.data && result.data.success && result.data.success.data) {
      const { data } = result.data.success;

      yield put(actions.loadDataSuccess({ superannuation: data }));
    }
  } catch (error) {
    logger.error(error);
  }
}

function* edit({ payload }) {
  try {
    const { id } = payload;
    const { result, formInfoResult } = yield all({
      result: call(sendRequest, api.load, { id }, notifications.REQUEST_ERROR),
      formInfoResult: call(sendRequest, formInfoApi.load, { id }, notifications.REQUEST_ERROR),
    });

    if (
      result &&
      result.data &&
      result.data.success &&
      result.data.success.data &&
      formInfoResult &&
      formInfoResult.data &&
      formInfoResult.data.success &&
      formInfoResult.data.success.data
    ) {
      const formInfo = formInfoResult.data.success.data;
      const { default_superfund } = formInfo;
      const { data: superannuation } = result.data.success;
      const { account_name, superfund, superfund_member_number } = superannuation;
      const { abn, usi, name } = superfund;

      yield put(
        actions.editSuccess({
          superannuation: {
            account_name,
            name: name || default_superfund.name,
            superfund_member_number,
            abn: abn || default_superfund.abn,
            usi: usi || default_superfund.usi,
          },
          formInfo,
        }),
      );
    }
  } catch (error) {
    logger.error(error);
  }
}

function* save({ payload }) {
  try {
    const { superfundSuggestions, abnSuggestions, editForm, id } = payload;

    const propsArray = Object.values(editForm);
    const superannuation = keyByProperty(propsArray, 'name', prop => prop.value);

    const { superfund_member_number, usi, name, abn, account_name } = superannuation;
    const usiValue = abnSuggestions.find(item => item.id === superannuation.usi);
    const abnValue = abnSuggestions.find(item => item.id === superannuation.abn);
    const nameValue = superfundSuggestions.find(item => item.id === superannuation.name);

    const superannuation_information = {
      superfund_member_number,
      abn: abnValue ? abnValue.abn : abn,
      usi: usiValue ? usiValue.usi : usi,
      name: nameValue ? nameValue.name : name,
      account_name,
    };

    const result = yield call(
      sendRequest,
      api.createItem,
      {
        data: { superannuation_information },
        id,
      },
      notifications.REQUEST_ERROR,
    );

    if (result?.data) {
      yield put(actions.saveSuccess());

      yield put(push(`/applicants/${id}?tab[]=superannuations`));

      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.success_headline'),
          type: 'success',
          message: t('dash.common.notification.edited_successfully', {
            item: t('dash.applicants.superannuation.superannuations_header'),
          }),
        }),
      );
    } else {
      yield put(actions.saveError({ data: { ...result } }));
    }
  } catch (error) {
    logger.error(error);
  }
}

function* loadSuperfundSuggestions({ payload }) {
  try {
    const { id, filter } = payload;
    const result = yield call(
      sendRequest,
      apiSuperfundList.load,
      { id, search: { filter } },
      notifications.REQUEST_ERROR,
    );

    if (result && result.data && result.data.success && result.data.success.data) {
      yield put(actions.loadSuperfundSuggestionsSuccess(result.data.success.data.records));
    }
  } catch (error) {
    logger.error(error);
  }
}

function* loadAbnSuggestions({ payload }) {
  try {
    const { id, filter } = payload;
    const result = yield call(
      sendRequest,
      apiSuperfundList.load,
      { id, search: { filter } },
      notifications.REQUEST_ERROR,
    );

    if (result && result.data && result.data.success && result.data.success.data) {
      yield put(actions.loadAbnSuggestionsSuccess(result.data.success.data.records));
    }
  } catch (error) {
    logger.error(error);
  }
}

export default function* rootSaga() {
  yield all([
    takeEvery(actions.LOAD_REQUEST, loadDataRequest),
    takeEvery(actions.EDIT, edit),
    takeEvery(actions.SAVE, save),
    takeEvery(actions.LOAD_SUPERFUND_SUGGESTIONS_REQUEST, loadSuperfundSuggestions),
    takeEvery(actions.LOAD_ABN_SUGGESTIONS_REQUEST, loadAbnSuggestions),
  ]);
}
